import React, { useEffect, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  Input,
  Label,
  FormGroup,
  Form,
  Spinner,
} from "reactstrap";
import Select from "react-select";
import { toast } from "react-toastify";
import * as Yup from "yup";
import {
  addJobCategory,
  getJobCategoryDataById,
  updateJobCategory,
} from "model/CategoryModel";
import { getCommonConversationMatrixGroupOptions } from "model/ClientRegisterModel";
import { allowOnlyNumber } from "helpers/GeneralHelper";
import { selectMenuStyles } from "pages/AllCommonModal/AIScheduleMatrixAddEditModal/ai-schedule-matrix-add-edit-modal";

const selectZIndexStyles = selectMenuStyles;

const normalizeCommonList = (response) =>
  response?.data?.records ||
  response?.data?.data ||
  response?.data ||
  response ||
  [];

const mapConversationMatrixGroupOptions = (response) =>
  (Array.isArray(normalizeCommonList(response))
    ? normalizeCommonList(response)
    : []
  )
    .map((item) => {
      const value = String(item?.id ?? item?.Id ?? item?.value ?? "").trim();
      const label = String(
        item?.display ?? item?.name ?? item?.label ?? item?.title ?? value
      ).trim();
      if (!value) return null;
      return { value, label: label || value };
    })
    .filter(Boolean);

const INCENTIVE_AMOUNT_FIELDS = [
  "recruiterIncentiveAmount",
  "backendIncentiveAmount",
];

const incentiveAmountRequiredSchema = (label) =>
  Yup.string()
    .trim()
    .required(`${label} is required`);

/* -------------------- Yup Schema -------------------- */
const jobCategorySchema = Yup.object().shape({
  name: Yup.string()
    .trim()
    .required("Job category name is required"),

  incentive: Yup.string()
    .trim()
    .required("Incentive type is required"),

  recruiterIncentiveAmount: Yup.string().when("incentive", {
    is: "on hire",
    then: () => incentiveAmountRequiredSchema("Recruiter incentive amount"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  backendIncentiveAmount: Yup.string().when("incentive", {
    is: "on hire",
    then: () => incentiveAmountRequiredSchema("Backend incentive amount"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),

  // vTitle: Yup.string()
  //   .trim()
  //   .required("Video title is required"),

  // vLink: Yup.string()
  //   .trim()
  //   .url("Enter a valid URL")
  //   .required("Video link is required"),
  quota: Yup.boolean()
    .required("Quota selection is required"),

  conversationGroupId: Yup.mixed()
    .required("Conversation matrix group is required")
    .test(
      "non-empty",
      "Conversation matrix group is required",
      (value) =>
        value !== null &&
        value !== undefined &&
        String(value).trim() !== ""
    ),

  status: Yup.boolean(),
});

const JobCategoryAddEditModal = ({
  setJobCategoryAddEditModalOpen,
  jobCategoryAddEditModalOpen,
  getJobCategoryList,
  itemId,
  setFormData,
  formData,
  totalData
}) => {
  const [formKey, setFormKey] = useState(0);
  const [errors, setErrors] = useState({});
  const [conversationMatrixGroupOptions, setConversationMatrixGroupOptions] =
    useState([]);
  const [loadingConversationGroups, setLoadingConversationGroups] =
    useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getSelectedOption = (options, value) =>
    options.find((option) => String(option?.value) === String(value)) || null;

  const closeModal = () => {
    if (submitting) return;
    setJobCategoryAddEditModalOpen(false);
    setErrors({});
  };

  const handleConversationGroupChange = async (selectedOption) => {
    const nextId = selectedOption?.value ?? null;
    const nextFormData = {
      ...formData,
      conversationGroupId: nextId,
    };
    setFormData(nextFormData);
    try {
      await jobCategorySchema.validateAt(
        "conversationGroupId",
        nextFormData
      );
      setErrors((prev) => ({ ...prev, conversationGroupId: "" }));
    } catch (err) {
      setErrors((prev) => ({
        ...prev,
        conversationGroupId: err.message,
      }));
    }
  };

  const isOnHireIncentive = formData?.incentive === "on hire";

  /* -------------------- Change Handler -------------------- */
  const handleChange = async (e) => {
    const { name, type, checked, value } = e.target;
    let fieldValue = type === "checkbox" ? checked : value;

    if (INCENTIVE_AMOUNT_FIELDS.includes(name)) {
      fieldValue = allowOnlyNumber(value);
    }

    const nextFormData = {
      ...formData,
      [name]: fieldValue,
    };

    setFormData(nextFormData);

    if (name === "incentive" && value !== "on hire") {
      setErrors((prev) => ({
        ...prev,
        recruiterIncentiveAmount: "",
        backendIncentiveAmount: "",
      }));
    }

    if (!jobCategorySchema.fields[name]) return;

    try {
      await jobCategorySchema.validateAt(name, nextFormData);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  /* -------------------- Submit -------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      await jobCategorySchema.validate(formData, { abortEarly: false });
      setErrors({});
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((error) => {
        newErrors[error.path] = error.message;
      });
      setErrors(newErrors);
      return;
    }

    const isOnHire = formData?.incentive === "on hire";
    const {
      existingFedexIncentiveAmount: _removedFedexAmount,
      ...formWithoutFedex
    } = formData || {};
    const resolvedConversationGroupId = String(
      formData?.conversationGroupId ?? ""
    ).trim();

    const params = {
      ...formWithoutFedex,
      conversationGroupId: resolvedConversationGroupId || null,
      status: formData?.status ? "active" : "deactive",
      recruiterIncentiveAmount: isOnHire
        ? formData?.recruiterIncentiveAmount
        : null,
      backendIncentiveAmount: isOnHire
        ? formData?.backendIncentiveAmount
        : null,
    };

    setSubmitting(true);
    try {
      let response;
      if (itemId) {
        response = await updateJobCategory(itemId, params);
      } else {
        response = await addJobCategory(params);
      }

      if (response?.success) {
        toast.success(response?.message);
        setJobCategoryAddEditModalOpen(false);
        setErrors({});
        getJobCategoryList();
      } else {
        toast.error(response?.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /* -------------------- Edit Fetch -------------------- */
  const getFetchJobCategoryById = async (id) => {
    const response = await getJobCategoryDataById({ Id: id });
    if (response?.data) {
      setFormData({
        ...response?.data,
        status:
          response?.data?.status === "active",
        quota: Boolean(response?.data?.quota),
        recruiterIncentiveAmount:
          response?.data?.recruiterIncentiveAmount ?? "",
        backendIncentiveAmount:
          response?.data?.backendIncentiveAmount ?? "",
        conversationGroupId:
          response?.data?.conversationGroupId ??
          response?.data?.ConversationGroupId ??
          null,
      });
      setFormKey((k) => k + 1);
    }
  };

  useEffect(() => {
    if (itemId) {
      getFetchJobCategoryById(itemId);
    }
  }, [itemId]);

  useEffect(() => {
    if (!jobCategoryAddEditModalOpen) return;

    let cancelled = false;

    const loadConversationMatrixGroups = async () => {
      setLoadingConversationGroups(true);
      try {
        const response = await getCommonConversationMatrixGroupOptions();
        if (!cancelled) {
          setConversationMatrixGroupOptions(
            mapConversationMatrixGroupOptions(response)
          );
        }
      } finally {
        if (!cancelled) setLoadingConversationGroups(false);
      }
    };

    loadConversationMatrixGroups();

    return () => {
      cancelled = true;
    };
  }, [jobCategoryAddEditModalOpen]);

  return (
    <Modal
      isOpen={jobCategoryAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      className="modal-full-height"
      centered
    >
      <ModalBody
        key={formKey}
        className="p-4"
        style={{ maxHeight: "95vh", overflowY: "auto" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="fw-bold mb-0">{itemId? 'Edit' : 'Create'} Job Category</h5>
          <Button close onClick={closeModal} disabled={submitting} />
        </div>
        <hr />

        <Form onSubmit={handleSubmit}>
          {/* Name */}
          <FormGroup>
            <Label>Name <span className="text-danger">*</span></Label>
            <Input
              type="text"
              name="name"
              defaultValue={formData?.name}
              onChange={handleChange}
            />
            {errors.name && (
              <small className="text-danger">{errors.name}</small>
            )}
          </FormGroup>

          {/* Incentive Type */}
          <FormGroup>
            <Label>Incentive Type <span className="text-danger">*</span></Label>
            <Input
              type="select"
              name="incentive"
              defaultValue={formData?.incentive}
              onChange={handleChange}
            >
              <option value="">Select Incentive Type</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="on hire">On Hire</option>
            </Input>
            {errors.incentive && (
              <small className="text-danger">{errors.incentive}</small>
            )}
          </FormGroup>

          {isOnHireIncentive && (
            <>
              {/* Recruiter Incentive Amount */}
              <FormGroup>
                <Label>
                  Recruiter Incentive Amount{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="recruiterIncentiveAmount"
                  value={formData?.recruiterIncentiveAmount ?? ""}
                  onChange={handleChange}
                />
                {errors.recruiterIncentiveAmount && (
                  <small className="text-danger">
                    {errors.recruiterIncentiveAmount}
                  </small>
                )}
              </FormGroup>

              {/* Backend Incentive Amount */}
              <FormGroup>
                <Label>
                  Backend Incentive Amount{" "}
                  <span className="text-danger">*</span>
                </Label>
                <Input
                  type="text"
                  name="backendIncentiveAmount"
                  value={formData?.backendIncentiveAmount ?? ""}
                  onChange={handleChange}
                />
                {errors.backendIncentiveAmount && (
                  <small className="text-danger">
                    {errors.backendIncentiveAmount}
                  </small>
                )}
              </FormGroup>
            </>
          )}

          {/* Conversation Matrix Group */}
          <FormGroup>
            <Label>
              Conversation Matrix Group{" "}
              <span className="text-danger">*</span>
            </Label>
            <Select
              options={conversationMatrixGroupOptions}
              className="select2-selection"
              placeholder="Select conversation matrix group"
              isLoading={loadingConversationGroups}
              isDisabled={submitting}
              value={getSelectedOption(
                conversationMatrixGroupOptions,
                formData?.conversationGroupId
              )}
              onChange={handleConversationGroupChange}
              menuPortalTarget={
                typeof document !== "undefined" ? document.body : null
              }
              menuPosition="fixed"
              styles={selectZIndexStyles}
            />
            {errors.conversationGroupId && (
              <small className="text-danger">
                {errors.conversationGroupId}
              </small>
            )}
          </FormGroup>

          {/* Video Title */}
          <FormGroup>
            <Label>Video Title</Label>
            <Input
              type="text"
              name="vTitle"
              defaultValue={formData?.vTitle}
              onChange={handleChange}
            />
            {errors.vTitle && (
              <small className="text-danger">{errors.vTitle}</small>
            )}
          </FormGroup>

          {/* Video Link */}
          <FormGroup>
            <Label>Video Link</Label>
            <Input
              type="text"
              name="vLink"
              defaultValue={formData?.vLink}
              onChange={handleChange}
            />
            {errors.vLink && (
              <small className="text-danger">{errors.vLink}</small>
            )}
          </FormGroup>

          {/* Quota */}
          <FormGroup>
            <Label>Quota <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div>
                <Input
                  type="radio"
                  name="quota"
                  defaultChecked={formData?.quota === true}
                  onChange={() =>
                    setFormData((p) => ({ ...p, quota: true }))
                  }
                />{" "}
                Yes
                <Input
                  type="radio"
                  name="quota"
                  defaultChecked={formData?.quota === false}
                  onChange={() =>
                    setFormData((p) => ({ ...p, quota: false }))
                  }
                  className="ms-3"
                />{" "}
                No
              </div>
            </div>
          </FormGroup>

          {/* Status */}
          <FormGroup>
            <Label>Status <span className="text-danger">*</span></Label>
            <div className="input-box d-flex align-items-center justify-content-between">
              <span className="text-muted">Type</span>
              <div className="d-flex align-items-center">
                <strong className="me-2">
                  {formData?.status ? "Active" : "Deactive"}
                </strong>
                <div className="form-switch">
                  <Input
                    type="switch"
                    name="status"
                    defaultChecked={formData?.status}
                    onChange={handleChange}
                    className="form-check-input-padding"
                  />
                </div>
              </div>
            </div>
          </FormGroup>

          <div className="text-end mt-3">
            <Button color="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {itemId ? "Updating..." : "Saving..."}
                </>
              ) : itemId ? (
                "Update"
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default JobCategoryAddEditModal;
